import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type OrgFunctionKind, type OrgFunctionMember } from '@bliks-io/contracts';
import { useOrgFunction, useOrgFunctionMembers } from '@/api/queries/useOrgFunctions';
import { notFoundOrUnknownKey } from '@/api/errors';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { BackButton } from '@/components/back-button';
import { orgFunctionCatalogRoute, orgFunctionCopyPrefix } from '@/lib/orgFunctionRoutes';
import { leadLabelKeys } from './utils/leadLabelKeys';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  EntityDetailTitle,
  OrgFunctionKindBadge,
  getColorSchemeFromString,
  Skeleton,
  Page,
  PageContent,
  UserCard,
  DataTable,
  createColumnHeader,
  formatDate,
  renderFallback,
  type DataTableColumnDef
} from '@bliks-io/ui';

interface OrgFunctionDetailPageProps {
  orgFunctionId: string;
  /**
   * The kind of the catalog page this detail was reached from (route-known) —
   * drives the back-links and the loading/error copy before the entity itself
   * is available. The rendered kind badge and lead labels always come from the
   * loaded entity's own `kind`.
   */
  kind: OrgFunctionKind;
}

export function OrgFunctionDetailPage({ orgFunctionId, kind }: OrgFunctionDetailPageProps) {
  const { t } = useTranslation();
  const copyPrefix = orgFunctionCopyPrefix(kind);
  const slug = useAuthStore().slug();
  const { data: orgFunction, isPending, error } = useOrgFunction(orgFunctionId);
  const { data: members, isLoading: membersLoading } = useOrgFunctionMembers(orgFunctionId);

  useDocumentTitle('pageTitles.orgFunctionDetail', { name: orgFunction?.name ?? '' });

  // Derived member list (computed server-side from mainRole/secondaryRoles) —
  // a lead who also holds via one of those links carries an isLead badge; a
  // lead without such a link is not listed.
  const memberColumns = useMemo<DataTableColumnDef<OrgFunctionMember>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        header: createColumnHeader(t('common.Name')),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <UserCard
              user={{
                firstName: row.original.firstName,
                lastName: row.original.lastName,
                email: row.original.email,
                avatar: row.original.avatar,
                role: row.original.mainRole,
                departmentAbbreviation: row.original.department?.abbreviation
              }}
              variant="with-role"
            />
            {row.original.isLead && (
              <Badge shape="rounded" colorScheme="blue">
                {t('organization.orgFunctions.members.leadBadge')}
              </Badge>
            )}
          </div>
        )
      },
      {
        accessorKey: 'email',
        header: createColumnHeader(t('common.Email'))
      }
    ],
    [t]
  );

  if (isPending) {
    return (
      <Page>
        <PageContent>
          <Skeleton className="w-64 h-8 mb-8" />
          <Card>
            <CardHeader>
              <Skeleton className="w-48 h-6" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-2/3 h-4" />
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  if (error || !orgFunction) {
    return (
      <Page>
        <PageContent>
          <div className="flex items-center gap-4 mb-8">
            <BackButton to={orgFunctionCatalogRoute(kind)} params={{ slug }} />
          </div>
          <Card>
            <CardContent className="p-6">
              <p className="text-destructive">
                {t(notFoundOrUnknownKey(error, `${copyPrefix}.notFound`))}
              </p>
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageContent>
        <div className="flex items-center gap-4 mb-8">
          <BackButton to={orgFunctionCatalogRoute(kind)} params={{ slug }} />
          <div>
            <EntityDetailTitle name={orgFunction.name} deletedAt={orgFunction.deletedAt} />
            <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
              {t(`${copyPrefix}.detailDescription`)}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('organization.orgFunctions.details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Name')}</label>
                <p className="mt-1 text-base">{orgFunction.name}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">
                  {t('organization.orgFunctions.kind.label')}
                </label>
                <div className="mt-1">
                  <OrgFunctionKindBadge
                    kind={orgFunction.kind}
                    label={t(`organization.orgFunctions.kind.${orgFunction.kind}` as const)}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">
                  {t('organization.orgFunctions.email')}
                </label>
                <p className="mt-1 text-base">{renderFallback(orgFunction.email)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Description')}</label>
                <p className="mt-1 text-base">{renderFallback(orgFunction.description)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">
                  {t(leadLabelKeys(orgFunction.kind).label)}
                </label>
                <p className="mt-1 text-base">
                  {renderFallback(
                    orgFunction.lead
                      ? `${orgFunction.lead.firstName} ${orgFunction.lead.lastName}`
                      : undefined
                  )}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">
                  {t('organization.orgFunctions.department')}
                </label>
                <div className="mt-1">
                  {orgFunction.department ? (
                    <div className="flex items-center gap-2">
                      <span>{orgFunction.department.name}</span>
                      <Badge
                        shape="rounded"
                        colorScheme={getColorSchemeFromString(orgFunction.department._id)}
                      >
                        {orgFunction.department.abbreviation}
                      </Badge>
                    </div>
                  ) : (
                    renderFallback(undefined)
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="text-sm font-medium text-gray-8">{t('common.Created')}</label>
                  <p className="mt-1 text-base">{formatDate(orgFunction.createdAt)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-8">{t('common.Updated')}</label>
                  <p className="mt-1 text-base">{formatDate(orgFunction.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('organization.orgFunctions.members.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={memberColumns}
                data={members ?? []}
                isLoading={membersLoading}
                emptyMessage={t('organization.orgFunctions.members.empty')}
                enableGlobalFilter
                globalSearchPlaceholder={t('organization.orgFunctions.members.search')}
                tableId="orgFunction-members"
              />
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </Page>
  );
}
